import React, { useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';

// Due Diligence follow-on services (Calem, Bugify #9f5a511d). Picking the catalogue's Due Diligence on a quotation or
// invoice line asks, once for that line, which of the services that follow it the client also wants (InvoiceForm).
// Each tick becomes a line of its own right after the Due Diligence line, built as picking that service from the list
// builds one; its Job # is left for the user. Calem's answer (opening #2): Contract Review is one line per agreement,
// the agreement written under it; Draft Contract is one line per agreement, each its own catalogue service; Lease
// Appraisal is asked only once a lease agreement is ticked; Transfer of Ownership and Lease Registration are each one
// of two.
//
// `names` are catalogue names, tried in order; an option whose service the document's company does not list is not
// offered. Agreement names are the document's words, so they stay English in every language.
type Opt = { key: string; names: string[]; label?: string; note?: string; lease?: boolean };
type Group = { key: string; head?: string; one?: boolean; opts: Opt[] };
const GROUPS: Group[] = [
  { key: 'poa', opts: [{ key: 'poa', names: ['Power of Attorney (POA)'] }] },
  { key: 'review', head: 'iv_dd_more_review', opts: [
    { key: 'review_res', names: ['Contract Review'], note: 'Reservation Agreement' },
    { key: 'review_sp', names: ['Contract Review'], note: 'Sale & Purchase Agreement' },
    { key: 'review_lease', names: ['Contract Review'], note: 'Lease Agreement', lease: true },
  ] },
  { key: 'draft', head: 'iv_dd_more_draft', opts: [
    { key: 'draft_sp', names: ['Draft Contract (Sale & Purchase Agreement)', 'Sale & Purchase Agreement'], label: 'Sale & Purchase Agreement' },
    { key: 'draft_lease', names: ['Draft Contract (Lease Agreement)', 'Lease Agreement'], label: 'Lease Agreement', lease: true },
  ] },
  { key: 'appraisal', opts: [{ key: 'appraisal', names: ['Lease Appraisal'] }] },
  { key: 'transfer', one: true, opts: [
    { key: 'transfer', names: ['Transfer of Ownership'] },
    { key: 'transfer_check', names: ['Transfer of Ownership (Check)'] },
  ] },
  { key: 'lease_reg', one: true, opts: [
    { key: 'lease_reg', names: ['Lease Registration'] },
    { key: 'lease_reg_check', names: ['Registration of Lease (Check)'] },
  ] },
  { key: 'will', opts: [{ key: 'will', names: ['Last Will & Testament'] }] },
];

// Catalogue names compared without case, punctuation or the '&' / 'and' difference, so "Last Will & Testament" finds
// "Last Will and Testament" — but "Transfer of Ownership" never finds "Transfer of Ownership (Check)".
const norm = (s: any) => String(s || '').toLowerCase().replace(/&amp;|&/g, ' and ').replace(/[^a-z0-9]+/g, ' ').trim();
export const isDueDiligence = (name?: string) => norm(name) === 'due diligence';

// The first of `names` the catalogue has, an active row before an inactive one (as Lease Registration's drafts pick).
const findService = (services: any[], names: string[]): any => {
  for (const n of names) {
    const hits = services.filter(s => norm(s.name) === norm(n));
    const hit = hits.find(s => Number(s.active ?? 1) !== 0) || hits[0];
    if (hit) return hit;
  }
  return null;
};

type Offer = Omit<Group, 'opts'> & { opts: (Opt & { svc: any })[] };
export type DdPick = { svc: any; note: string };

/** What this catalogue can offer: each option with its service, empty groups dropped — and Lease Appraisal too when no
 *  lease agreement can be ticked to show it. Nothing left = nothing to ask. */
export function ddFollowOnsIn(services: any[]): Offer[] {
  const offers: Offer[] = GROUPS
    .map(g => ({ ...g, opts: g.opts.map(o => ({ ...o, svc: findService(services, o.names) })).filter(o => o.svc) }))
    .filter(g => g.opts.length);
  return offers.some(g => g.opts.some(o => o.lease)) ? offers : offers.filter(g => g.key !== 'appraisal');
}

export default function DdFollowOnsDialog({ services, fmt, onCancel, onAdd }: { services: any[]; fmt: (n: any) => string; onCancel: () => void; onAdd: (picks: DdPick[]) => void }) {
  const { t } = useTranslation();
  const offers = useMemo(() => ddFollowOnsIn(services), [services]);
  const [ticked, setTicked] = useState<Record<string, boolean>>({});
  // Lease Appraisal shows only while a lease agreement is ticked, and only what shows is added.
  const leaseTicked = offers.some(g => g.opts.some(o => o.lease && ticked[o.key]));
  const shown = offers.filter(g => g.key !== 'appraisal' || leaseTicked);
  const picks: DdPick[] = shown.flatMap(g => g.opts.filter(o => ticked[o.key]).map(o => ({ svc: o.svc, note: o.note || '' })));
  // In a one-of-two group a tick clears the other.
  const toggle = (g: Offer, key: string) => setTicked(prev => ({
    ...prev, ...(g.one && !prev[key] ? Object.fromEntries(g.opts.map(o => [o.key, false])) : {}), [key]: !prev[key],
  }));
  const lbl: React.CSSProperties = { fontSize: 11, fontWeight: 700, textTransform: 'uppercase', letterSpacing: '.03em', color: 'var(--text-secondary)', marginBottom: 2 };
  return (
    <div style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.45)', zIndex: 10060, display: 'flex', alignItems: 'center', justifyContent: 'center' }} onMouseDown={e => e.target === e.currentTarget && onCancel()}>
      <div style={{ width: 480, maxWidth: '92vw', maxHeight: '90vh', overflowY: 'auto', background: 'var(--bg-surface)', borderRadius: 14, padding: 22, boxShadow: 'var(--shadow-md)' }}>
        <div style={{ fontSize: 16, fontWeight: 800, color: 'var(--text-primary)' }}>{t('iv_dd_more_title')}</div>
        <div style={{ fontSize: 12, color: 'var(--text-tertiary)', marginTop: 3, marginBottom: 10, lineHeight: 1.5 }}>{t('iv_dd_more_hint')}</div>
        {shown.map((g, gi) => (
          <div key={g.key} style={{ padding: '8px 0', borderTop: gi ? '1px solid var(--border)' : 'none' }}>
            {g.head && <div style={lbl}>{t(g.head)}</div>}
            {g.opts.map((o, oi) => (
              <React.Fragment key={o.key}>
                {g.one && oi > 0 && <div style={{ paddingLeft: 24, fontSize: 11, color: 'var(--text-tertiary)' }}>{t('iv_dd_more_or')}</div>}
                <label style={{ display: 'flex', alignItems: 'center', gap: 9, padding: '4px 0', fontSize: 13, cursor: 'pointer' }}>
                  <input type="checkbox" checked={!!ticked[o.key]} onChange={() => toggle(g, o.key)} style={{ width: 15, height: 15, flexShrink: 0 }} />
                  <span style={{ flex: 1, fontWeight: 600, color: 'var(--text-primary)' }}>{o.label || o.note || o.svc.name}</span>
                  <span style={{ color: 'var(--text-tertiary)', whiteSpace: 'nowrap' }}>{fmt(o.svc.price)}</span>
                </label>
              </React.Fragment>
            ))}
          </div>
        ))}
        <div style={{ display: 'flex', gap: 10, marginTop: 14, justifyContent: 'flex-end' }}>
          <button onClick={onCancel} style={{ padding: '9px 18px', borderRadius: 10, border: 'none', background: 'var(--c-gray-100)', color: 'var(--text-secondary)', fontWeight: 700, fontSize: 13, cursor: 'pointer' }}>{t('cancel')}</button>
          <button onClick={() => picks.length && onAdd(picks)} disabled={!picks.length}
            style={{ padding: '9px 20px', borderRadius: 10, border: 'none', background: 'var(--accent)', color: '#fff', fontWeight: 700, fontSize: 13, cursor: picks.length ? 'pointer' : 'default', opacity: picks.length ? 1 : 0.5 }}>{t('iv_dd_more_add', { n: picks.length })}</button>
        </div>
      </div>
    </div>
  );
}
